// /web/src/pages/projects/EditProject.jsx
import React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useApi } from "../../lib/api";

const occupancyOptions = ["6", "8", "10", "12.5", "15"];

export default function EditProject() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);
  const [form, setForm] = React.useState({
    name: "",
    postcode: "",
    local_authority: "",
    building_use: "office",
    occupancy_ratio: "10",
    floors_count: "",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [lookupNote, setLookupNote] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const project = await api(`/projects/${id}`);
        if (cancelled) return;
        setForm({
          name: project?.name ?? "",
          postcode: project?.postcode ?? "",
          local_authority: project?.local_authority ?? "",
          building_use: project?.building_use || "office",
          occupancy_ratio:
            project?.occupancy_ratio === null || project?.occupancy_ratio === undefined
              ? "10"
              : String(project.occupancy_ratio),
          floors_count:
            project?.floors_count === null || project?.floors_count === undefined
              ? ""
              : String(project.floors_count),
        });
        setError("");
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load project");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, id]);

  function onChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  function normalisePostcode(pc) {
    return (pc || "")
      .toUpperCase()
      .replace(/\s+/g, "")
      .replace(/^UK/, "")
      .trim();
  }

  async function lookupLocalAuthority() {
    const raw = form.postcode;
    const pc = normalisePostcode(raw);
    if (!pc) return;

    try {
      const data = await api(`/geo/postcodes/${encodeURIComponent(pc)}`);
      const la = data?.local_authority || "";
      const postcode = data?.postcode || raw;
      setForm((f) => ({ ...f, postcode, local_authority: la }));
      setError("");
      setLookupNote(data?.lookup_note || "");
    } catch (err) {
      setLookupNote("");
      const message = err?.message || "";
      if (message.startsWith("Network error calling API")) {
        setError("Postcode lookup failed. Check your connection and try again.");
      } else if (message) {
        setError(message);
      } else {
        setError("Postcode lookup failed. Check your connection and try again.");
      }
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (loading || submitting) return;

    setSubmitting(true);
    setError("");
    setLookupNote("");

    try {
      const body = {
        name: form.name.trim(),
        building_use: form.building_use || "office",
        postcode: form.postcode || null,
        local_authority: form.local_authority || null,
        occupancy_ratio:
          form.occupancy_ratio !== "" && form.occupancy_ratio !== null
            ? Number(form.occupancy_ratio)
            : null,
        floors_count:
          form.floors_count !== "" && form.floors_count !== null
            ? Number(form.floors_count)
            : null,
      };

      if (!body.name) throw new Error("Please enter a project name.");

      await api(`/projects/${id}`, { method: "PUT", body });
      navigate(`/projects/${id}`);
    } catch (err) {
      setError(err?.message || "Failed to update project");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="container" style={{ padding: "28px 0 40px", maxWidth: 760 }}>
        <h1 style={{ marginTop: 0 }}>Edit project</h1>
        <p>Loading project…</p>
      </div>
    );
  }

  const occupancyValue = form.occupancy_ratio ?? "";
  const showCustomOccupancy =
    occupancyValue !== "" && !occupancyOptions.includes(String(occupancyValue));

  return (
    <div className="container" style={{ padding: "28px 0 40px", maxWidth: 760 }}>
      <h1 style={{ marginTop: 0 }}>Edit project</h1>
      {error && <div style={alertStyle}><strong>Error:</strong> {error}</div>}
      {!error && lookupNote && (
        <div style={infoStyle}>
          <strong>Heads up:</strong> {lookupNote}
        </div>
      )}

      <form onSubmit={onSubmit} className="card" style={{ padding: 16 }}>
        {/* Name */}
        <div style={{ display: "grid", gap: 6 }}>
          <label>
            <div className="small">Name</div>
            <input
              required
              name="name"
              value={form.name}
              onChange={onChange}
              placeholder="e.g. 105 Old Lane"
              style={inputStyle}
            />
          </label>
        </div>

        {/* GAP */}
        <div style={{ height: 12 }} />

        {/* Postcode + LA */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12 }}>
          <label>
            <div className="small">Postcode</div>
            <input
              name="postcode"
              value={form.postcode}
              onChange={onChange}
              onBlur={lookupLocalAuthority}
              placeholder="SW1A 1AA"
              style={inputStyle}
              autoCapitalize="characters"
            />
          </label>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={lookupLocalAuthority}
            style={{ alignSelf: "end", height: 42 }}
          >
            Lookup
          </button>
        </div>

        <label style={{ marginTop: 8 }}>
          <div className="small">Local authority</div>
          <input
            name="local_authority"
            value={form.local_authority}
            onChange={onChange}
            placeholder="Auto-populated from postcode (editable)"
            style={inputStyle}
          />
        </label>

        {/* GAP */}
        <div style={{ height: 12 }} />

        {/* Use + Occupancy + Floors */}
        <label>
          <div className="small">Building use</div>
          <select
            name="building_use"
            value={form.building_use}
            onChange={onChange}
            style={inputStyle}
          >
            <option value="office">Office</option>
          </select>
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div className="small">Occupancy ratio</div>
            <select
              name="occupancy_ratio"
              value={form.occupancy_ratio}
              onChange={onChange}
              style={inputStyle}
            >
              {occupancyOptions.map((value) => (
                <option key={value} value={value}>
                  {`1:${value}`}
                </option>
              ))}
              {showCustomOccupancy && (
                <option value={occupancyValue}>{`Current (${occupancyValue})`}</option>
              )}
            </select>
            <div className="small" style={{ marginTop: 4, opacity: 0.7 }}>
              Stored as m²/person (e.g., 1:10 → 10)
            </div>
          </label>

          <label>
            <div className="small">Number of floors</div>
            <input
              type="number"
              min="1"
              step="1"
              name="floors_count"
              value={form.floors_count}
              onChange={onChange}
              placeholder="e.g. 10"
              style={inputStyle}
            />
          </label>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button className="btn btn--primary" disabled={submitting} type="submit">
            {submitting ? "Saving…" : "Save changes"}
          </button>
          <Link to={`/projects/${id}`} className="btn btn--ghost">Cancel</Link>
        </div>
      </form>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  marginTop: 6,
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid rgba(0,0,0,0.15)",
  font: "inherit",
};

const alertStyle = {
  marginBottom: 12,
  padding: "10px 12px",
  borderRadius: 8,
  background: "rgba(255, 59, 48, 0.08)",
  color: "#8a1c1c",
  border: "1px solid rgba(255, 59, 48, 0.2)",
};

const infoStyle = {
  marginBottom: 12,
  padding: "10px 12px",
  borderRadius: 8,
  background: "rgba(0, 122, 255, 0.08)",
  color: "#0f3d75",
  border: "1px solid rgba(0, 122, 255, 0.2)",
};
